import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { getData } from "../../../axios";
import { createErrorReportingAsyncThunk } from "../../helpers";
import {
  CalendarViewData,
  CalendarViewFilterType,
  calendarViewDataT,
} from "./types";
import { AppDispatch, AppThunkAction, RootState } from "../../../redux-store";
import { DateTime } from "luxon";
import { createCalendarViewTypes } from "dora-contracts";
import * as t from "io-ts";
import * as tPromise from "io-ts-promise";
import { debounce } from "lodash";
import { userStorage } from "../../../helpers/user-storage";

const { moveStopInRouteReqT } = createCalendarViewTypes({
  dateTime: t.string,
  date: t.string,
});

const prefix = "data/calendar-view";

// Saved per user and restored once the user is known (see checkAuthState).
export const CALENDAR_VIEW_PAGE_TEAM_FILTER = "calendarViewPageTeamFilter";
export const CALENDAR_VIEW_FILTER_TYPES: readonly CalendarViewFilterType[] = [
  "routes",
];

type State = {
  status: "initial" | "loading" | "success" | "error";
  data: CalendarViewData;
  startDate: DateTime;
  selectedInterval: "day" | "week";
  teamFilterOptions: Record<CalendarViewFilterType, string[]>;
  pageInView: boolean;
};

const initialState: State = {
  status: "initial",
  data: [],
  startDate: DateTime.utc(),
  selectedInterval: "day",
  pageInView: false,
  teamFilterOptions: { routes: [] },
};

export const debouncedLoadCalendarViewData = debounce(
  (dispatch: AppDispatch) => dispatch(loadCalendarViewData()),
  1000,
  { leading: true, trailing: true, maxWait: 1000 }
);

export const loadCalendarViewData = createErrorReportingAsyncThunk(
  `${prefix}/load-calendar-view-data`,
  async (_, { getState }) => {
    const state = getState() as RootState;
    const calendarViewState = state.data.calendarView;
    const interval = calendarViewState.selectedInterval;
    const startDate = calendarViewState.startDate;
    // Driver activities for these routes are loaded by the listener in
    // ../driver-activities/listeners.ts, once features are known.
    return axios
      .get(
        `/api/calendar-view?interval=${interval}&startDate=${startDate.toFormat("yyyy-MM-dd")}`
      )
      .then(getData)
      .then(tPromise.decode(calendarViewDataT));
  }
);

export const moveStopInRoute = createErrorReportingAsyncThunk(
  `${prefix}/update-stop-time`,
  async (
    input: {
      stopId: string;
      cargoId: string;
      routeId: string;
      date: string;
      time: string;
    },
    { getState }
  ) => {
    const { stopId, cargoId, date, routeId, time } = input;
    const state = getState() as RootState;
    const response = await axios.post(
      `/api/calendar-view/move-stop-in-route`,
      moveStopInRouteReqT.encode({
        stopId,
        cargoId,
        date,
        routeId,
        time,
        interval: state.data.calendarView.selectedInterval,
        startDate: state.data.calendarView.startDate.toFormat("yyyy-MM-dd"),
      })
    );
    return response.data;
  }
);

export const shiftCargoStopsTime = createErrorReportingAsyncThunk(
  `${prefix}/shift-cargo-stops-time`,
  async (
    {
      cargoId,
      minutesAmount,
    }: {
      cargoId: string;
      minutesAmount: number;
    },
    { getState }
  ) => {
    const state = getState() as RootState;
    const response = await axios.post(
      `/api/calendar-view/shift-cargo-stops-time`,
      {
        cargoId,
        minutesAmount,
        interval: state.data.calendarView.selectedInterval,
        startDate: state.data.calendarView.startDate.toFormat("yyyy-MM-dd"),
      }
    );
    return response.data;
  }
);

export const setFilterOptionsAndSave =
  (payload: {
    teamId: string;
    enabled: boolean;
    filterType: CalendarViewFilterType;
  }): AppThunkAction =>
  (dispatch: AppDispatch, getState) => {
    dispatch(slice.actions.setFilterOptions(payload));
    const state = getState();
    const teamFilterOptions = state.data.calendarView.teamFilterOptions;
    userStorage.setItem(
      CALENDAR_VIEW_PAGE_TEAM_FILTER,
      JSON.stringify(teamFilterOptions)
    );
  };

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    componentMount: (state) => {
      state.pageInView = true;
    },
    componentUnmount: (state) => {
      state.pageInView = false;
    },
    changeSelectedInterval: (state, action) => {
      const { interval, options } = action.payload;
      state.selectedInterval = interval;
      if (options?.startDate) {
        state.startDate = options?.startDate;
      }
    },
    increaseStartDate: (state) => {
      state.startDate = state.startDate.plus(
        state.selectedInterval === "day" ? { days: 1 } : { days: 7 }
      );
    },
    decreaseStartDate: (state) => {
      state.startDate = state.startDate.minus(
        state.selectedInterval === "day" ? { days: 1 } : { days: 7 }
      );
    },
    setFilterOptions: (
      state,
      action: PayloadAction<{
        teamId: string;
        enabled: boolean;
        filterType: CalendarViewFilterType;
      }>
    ) => {
      const { teamId, enabled, filterType } = action.payload;
      const filter = state.teamFilterOptions[filterType];
      if (enabled) {
        if (!filter.includes(teamId)) {
          filter.push(teamId);
        }
      } else {
        state.teamFilterOptions[filterType] = filter.filter(
          (x) => x !== teamId
        );
      }
    },
    setAllFilterOptions: (state, action: PayloadAction<string[]>) => {
      state.teamFilterOptions = {
        ...state.teamFilterOptions,
        routes: action.payload,
      };
    },
    setTeamFilterOptions: (
      state,
      action: PayloadAction<Record<CalendarViewFilterType, string[]>>
    ) => {
      state.teamFilterOptions = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadCalendarViewData.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(loadCalendarViewData.rejected, (state) => {
      state.status = "error";
    });
    builder.addCase(loadCalendarViewData.fulfilled, (state, action) => {
      state.status = "success";
      state.data = action.payload;
    });
    builder.addCase(moveStopInRoute.fulfilled, (state, action) => {
      state.data = action.payload;
    });
    builder.addCase(shiftCargoStopsTime.fulfilled, (state, action) => {
      state.data = action.payload;
    });
  },
});

export const { changeSelectedInterval, increaseStartDate, decreaseStartDate } =
  slice.actions;

export const changeSelectedIntervalAndLoadData =
  (interval: string, options?: { startDate: DateTime }) =>
  (dispatch: AppDispatch) => {
    dispatch(changeSelectedInterval({ interval, options }));
    dispatch(loadCalendarViewData());
  };

export const previousIntervalSelected = (dispatch: AppDispatch) => {
  dispatch(decreaseStartDate());
  dispatch(loadCalendarViewData());
};

export const nextIntervalSelected = (dispatch: AppDispatch) => {
  dispatch(increaseStartDate());
  dispatch(loadCalendarViewData());
};

export default slice.reducer;

export const {
  componentMount,
  componentUnmount,
  setAllFilterOptions,
  setTeamFilterOptions,
} = slice.actions;
